import { PickingInfo } from "@deck.gl/core"
import { useCallback, useEffect } from "react"
import { IconPortal } from "themes/default-tailwind/plugins/map-icons/IconPortal"
import { deckglEventEmitter } from "features/map/MapElements"
import useMouse from "tools/events/useMouse"
import * as Icons from "@nova/component-icons"
import { useAppDispatch } from "integrations/redux/hooks"
import { IconGhostId } from "@nova/constants"
import { createHotspot } from "features/pubsub/entityActions/cubes/hotspot"
import { useCursorRef } from "tools/events/useCursorProvider"
import { getGlobalMapRef } from "features/map/GlobalViewState"

const hotspotSize = 12
export const UserPlacedHotspots = ({
    flightId,
    cancelHotspotPlace,
}: {
    flightId: string
    cancelHotspotPlace: () => void
}) => {
    const dispatch = useAppDispatch()
    const { mouse, setMouseListener, isMouseListening } = useMouse()
    const { current } = useCursorRef()

    useEffect(() => {
        setMouseListener(true)
    }, [setMouseListener])

    useEffect(() => {
        const closeTool = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setMouseListener(false)
                cancelHotspotPlace()
            }
        }
        window.addEventListener("keydown", closeTool, { once: true })

        return () => {
            window.removeEventListener("keydown", closeTool)
        }
    }, [setMouseListener, cancelHotspotPlace])

    const placeHotspot = useCallback(
        async (shiftKey: boolean) => {
            if (!isMouseListening || !mouse) return
            if (!current) {
                setMouseListener(false)
                cancelHotspotPlace()
                return
            }

            const mapRef = getGlobalMapRef()?.current?.getMap()

            const coordinates = mapRef?.unproject([current.x, current.y])
            if (!coordinates) return

            dispatch(
                createHotspot({
                    data: {
                        longitude: coordinates.lng,
                        latitude: coordinates.lat,
                    },
                    context: { flightId },
                })
            )

            if (!shiftKey) {
                setMouseListener(false)
                cancelHotspotPlace()
            }
        },
        [isMouseListening, mouse, current, dispatch, flightId, setMouseListener, cancelHotspotPlace]
    )

    useEffect(() => {
        const onMapClick = (_info: PickingInfo, event: { srcEvent: PointerEvent }) => {
            void placeHotspot(event.srcEvent.shiftKey)
        }
        deckglEventEmitter.on("click", onMapClick)
        window.addEventListener("contextmenu", cancelHotspotPlace)

        return () => {
            deckglEventEmitter.off("click", onMapClick)
            window.removeEventListener("contextmenu", cancelHotspotPlace)
        }
    }, [placeHotspot, cancelHotspotPlace])

    return (
        <>
            {mouse && current?.id === "map" && (
                <IconPortal
                    position={{
                        top: (mouse.y as number) - 11,
                        left: (mouse.x as number) - 10,
                    }}
                    size={hotspotSize}
                >
                    <Icons.SmallIcon
                        Icon={Icons.HotsotPlaceable}
                        id={IconGhostId}
                        style={{ color: "rgb(227, 71, 82)" }}
                    />
                </IconPortal>
            )}
        </>
    )
}
